import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Shield, ArrowLeft, BookOpen, MapPin, FileText, Video, MessageCircle, Download } from 'lucide-react';

const ResourcesPage = () => {
  const navigate = useNavigate();

  const resources = [
    {
      category: 'Help & Documentation',
      icon: BookOpen,
      items: [
        { title: 'Getting Started Guide', desc: 'Complete guide to set up your RMM platform', link: '#' },
        { title: 'User Documentation', desc: 'Comprehensive documentation for all features', link: '#' },
        { title: 'Video Tutorials', desc: 'Step-by-step video guides', link: '#' },
        { title: 'FAQ', desc: 'Answers to frequently asked questions', link: '#' }
      ]
    },
    {
      category: 'Developer Resources',
      icon: FileText,
      items: [
        { title: 'API Documentation', desc: 'Complete API reference and examples', link: '#' },
        { title: 'Integration Guides', desc: 'Connect RMM Pro with your tools', link: '#' },
        { title: 'Webhooks', desc: 'Set up real-time event notifications', link: '#' },
        { title: 'Code Examples', desc: 'Sample code and scripts', link: '#' }
      ]
    },
    {
      category: 'Product Updates',
      icon: MapPin,
      items: [
        { title: 'Roadmap', desc: 'See what features we\'re working on', link: '/public/roadmap' },
        { title: 'Changelog', desc: 'Latest updates and improvements', link: '/public/changelog' },
        { title: 'Release Notes', desc: 'Detailed release documentation', link: '#' },
        { title: 'Beta Program', desc: 'Get early access to new features', link: '#' }
      ]
    },
    {
      category: 'Support',
      icon: MessageCircle,
      items: [
        { title: 'Submit Ticket', desc: 'Get help from our support team', link: '#' },
        { title: 'Community Forum', desc: 'Connect with other MSPs', link: '#' },
        { title: 'Live Chat', desc: 'Chat with support during business hours', link: '#' },
        { title: 'Status Page', desc: 'Check system status and uptime', link: '#' }
      ]
    }
  ];

  const quickLinks = [
    { title: 'Agent Downloads', icon: Download, desc: 'Download the latest RMM agent', action: () => navigate('/settings/agent-installer') },
    { title: 'Video Library', icon: Video, desc: 'Watch product demos and tutorials', action: () => {} },
    { title: 'Best Practices', icon: BookOpen, desc: 'Learn MSP best practices', action: () => {} },
    { title: 'Contact Support', icon: MessageCircle, desc: 'Get in touch with our team', action: () => {} }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="mr-2">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-800">RMM Pro</span>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={() => navigate('/login')}>
                Login
              </Button>
              <Button onClick={() => navigate('/signup')} className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Resources & Support</h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Everything you need to succeed with RMM Pro - guides, documentation, updates, and support
          </p>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {quickLinks.map((link, index) => (
              <Card key={index} className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer" onClick={link.action}>
                <CardContent className="p-6 text-center">
                  <link.icon className="w-10 h-10 text-blue-600 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-900 mb-1">{link.title}</h3>
                  <p className="text-sm text-slate-600">{link.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {resources.map((resource, catIndex) => (
              <div key={catIndex}>
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-3">
                      <resource.icon className="w-6 h-6 text-blue-600" />
                      <span>{resource.category}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {resource.items.map((item, itemIndex) => (
                        <button
                          key={itemIndex}
                          onClick={() => item.link.startsWith('/') ? navigate(item.link) : window.open(item.link)}
                          className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                        >
                          <h4 className="font-semibold text-slate-900 mb-1">{item.title}</h4>
                          <p className="text-sm text-slate-600">{item.desc}</p>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Updates Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Latest Updates</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>📅 Roadmap</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">
                  See what features we're working on and vote on what you'd like to see next
                </p>
                <Button variant="outline" onClick={() => navigate('/public/roadmap')}>
                  View Roadmap
                </Button>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>📝 Changelog</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">
                  Track all new features, improvements, and bug fixes in our detailed changelog
                </p>
                <Button variant="outline" onClick={() => navigate('/public/changelog')}>
                  View Changelog
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Support CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Need Help?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Our support team is available 24/7 to assist you
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-white text-blue-600 hover:bg-slate-100 text-lg px-8"
            >
              Contact Support
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-white text-white hover:bg-white/10 text-lg px-8"
            >
              Browse Documentation
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
